import React from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button";
import { getSetting, updateSetting } from "../../Services/Setting";
import BackButton from "../../Components/BackButton";
import { Success, Error } from "../../Components/Modal";
import "../Dashboard/Dashboard.css";

const SECTION_THEMES = {
    route: { icon: "fa-route", theme: "primary" },
    search: { icon: "fa-satellite-dish", theme: "secondary" },
    weight: { icon: "fa-weight-hanging", theme: "info" },
    firstDelivery: { icon: "fa-truck-loading", theme: "primary" },
    promo: { icon: "fa-gift", theme: "success" },
    penalty: { icon: "fa-hand-holding-usd", theme: "danger" },
    fees: { icon: "fa-hands-helping", theme: "warning" },
    insurance: { icon: "fa-shield-alt", theme: "success" },
    payout: { icon: "fa-calendar-alt", theme: "primary" },
    support: { icon: "fa-headset", theme: "info" },
};

const SUPPORT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SUPPORT_PHONE_REGEX = /^\+?[0-9][0-9\s\-()]{5,19}$/;

export default function EditSetting() {
    const navigate = useNavigate();
    const { data: settingData } = useQuery("get-setting", getSetting);
    const settings = settingData?.data?.data;

    const flattenedSettings = settings
        ? {
            ...settings,
            firstDeliveryOfferStatus: settings.firstDeliveryOfferStatus ?? false,
            firstDeliveryOfferType: settings.firstDeliveryOfferType ?? "percentage",
            firstDeliveryOfferDiscountPercent:
                settings.firstDeliveryOfferDiscountPercent ?? 15,
            radiusDistance: settings.radiusDistance ?? 0,
            radiusUnit: settings.radiusUnit ?? "miles",
            timeoutTime: settings.timeoutTime ?? 0,
            timeoutUnit: settings.timeoutUnit ?? "sec",
            isreviewDiscountEnabled: settings.isreviewDiscountEnabled ?? false,
            reviewDiscountOffer: settings.reviewDiscountOffer ?? 0,
            bookingNumber: settings.bookingNumber ?? 2,
            reviewDiscountOfferType: settings.reviewDiscountOfferType ?? "percentage",
            payoutCycleType: settings.nextPayoutCycleType ?? settings.payoutCycleType ?? "semi-monthly",
            payoutWeeklyDay: settings.nextPayoutWeeklyDay ?? settings.payoutWeeklyDay ?? "monday",
            payoutCycleSplitDay: settings.nextPayoutCycleSplitDay ?? settings.payoutCycleSplitDay ?? 15,
            payoutMonthlyDay: settings.nextPayoutMonthlyDay ?? settings.payoutMonthlyDay ?? 1,
            _activePayoutCycleType: settings.payoutCycleType ?? "semi-monthly",
            _activePayoutWeeklyDay: settings.payoutWeeklyDay ?? "monday",
            _activePayoutCycleSplitDay: settings.payoutCycleSplitDay ?? 15,
            _activePayoutMonthlyDay: settings.payoutMonthlyDay ?? 1,
            _payoutSettingsChangeEffectiveDate: settings.payoutSettingsChangeEffectiveDate ?? null,
            supportEmail: settings.supportEmail ?? "",
            supportPhone: settings.supportPhone ?? "",
        }
        : {};

    const validNumberRegex = /^(0|[1-9]\d*)(\.\d{1,2})?$/;

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        values: flattenedSettings,
    });

    const mutation = useMutation(updateSetting, {
        onSuccess: () => {
            Success("Rates Updated", "The pricing configuration has been updated successfully.");
            navigate("/settings");
        },
        onError: (err) => {
            Error("Save Failed", err?.response?.data?.message || "There was an error updating the rates. Please try again.");
        },
    });

    const submit = (data) => {
        const payload = {
            ...data,
            basePickupFee: 0,
            cancellationPolicyStatus: true,
            userCancellationFee: 0,
            freeCancellationTime: 0,
        };

        [
            "freeDistanceMiles", "pricePerMile",
            "adminCommission", "baseWeightLbs", "addWeightPlusLbs",
            "stepPrice", "fragileItemFee", "extraHelperFee", "insuranceValue",
            "radiusDistance", "timeoutTime", "maxDriverNotifications",
            "userCancellationFee", "driverCancellationFee", "cancellationTimeThreshold",
            "firstDeliveryDiscountPercent", "firstDeliveryDiscountApplyCount",
            "firstDeliveryOfferDiscountPercent",
            "afterAcceptanceCancellationFeePercent",
            "reviewDiscountOffer",
            "bookingNumber",
            "payoutCycleSplitDay",
            "payoutMonthlyDay",
        ].forEach((key) => {
            if (payload[key] !== undefined && payload[key] !== "") {
                payload[key] = parseFloat(payload[key]);
            }
        });

        // Remove internal display-only fields
        delete payload._activePayoutCycleType;
        delete payload._activePayoutWeeklyDay;
        delete payload._activePayoutCycleSplitDay;
        delete payload._activePayoutMonthlyDay;
        delete payload._payoutSettingsChangeEffectiveDate;

        mutation.mutate(payload);
    };

    const firstDeliveryOfferType = watch("firstDeliveryOfferType") || "percentage";
    const isFirstDeliveryOfferFree = firstDeliveryOfferType === "free";

    const renderFirstDeliveryOfferTypeSelector = () => (
        <div className="settings-promo-type-grid">
            {[
                {
                    value: "free",
                    title: "First Delivery Free",
                    text: "New user's first booking delivery fare is fully free.",
                    icon: "fa-gift",
                },
                {
                    value: "percentage",
                    title: "First Delivery Percentage",
                    text: "New user's first booking gets a percentage discount.",
                    icon: "fa-percent",
                },
            ].map((option) => (
                <label
                    key={option.value}
                    className={`settings-promo-type-option ${firstDeliveryOfferType === option.value ? "is-active" : ""}`}
                >
                    <input
                        type="radio"
                        value={option.value}
                        {...register("firstDeliveryOfferType")}
                    />
                    <span className="settings-promo-type-option__icon">
                        <i className={`fas ${option.icon}`} />
                    </span>
                    <span className="settings-promo-type-option__title">{option.title}</span>
                    <p className="settings-promo-type-option__text">{option.text}</p>
                </label>
            ))}
        </div>
    );

    const getError = (name) => {
        const parts = name.split(".");
        let error = errors;
        for (const part of parts) {
            error = error?.[part];
        }
        return error;
    };

    const renderSectionHeader = (title, subtitle, themeKey, action = null) => {
        const theme = SECTION_THEMES[themeKey];
        return (
            <div className={`settings-section-card__header ${action ? "settings-section-card__header--split" : ""}`}>
                <div className="d-flex align-items-center gap-3">
                    <div className={`settings-section-card__icon settings-section-card__icon--${theme.theme}`}>
                        <i className={`fas ${theme.icon}`} />
                    </div>
                    <div>
                        <h5 className="settings-section-card__title">{title}</h5>
                        {subtitle ? <p className="settings-section-card__subtitle mb-0">{subtitle}</p> : null}
                    </div>
                </div>
                {action}
            </div>
        );
    };

    const renderToggle = (name, enabledLabel = "Enabled", disabledLabel = "Disabled") => (
        <div className="settings-toggle-wrap">
            <span className={`settings-status-badge ${watch(name) ? "is-enabled" : "is-disabled"}`}>
                {watch(name) ? enabledLabel : disabledLabel}
            </span>
            <label className="settings-toggle">
                <input type="checkbox" className="settings-toggle__input" checked={watch(name) || false} {...register(name)} />
                <span className="settings-toggle__slider" />
            </label>
        </div>
    );

    const renderInput = (name, label, unit = "", colClass = "col-md-6") => {
        const error = getError(name);
        return (
            <div className={colClass}>
                <label className="settings-edit-label">{label}</label>
                <div className="settings-edit-input-wrap">
                    <input
                        type="text"
                        placeholder="0.00"
                        className={`settings-edit-input ${error ? "is-error" : ""}`}
                        {...register(name, {
                            required: "Required",
                            pattern: {
                                value: validNumberRegex,
                                message: "Invalid format",
                            },
                        })}
                    />
                    {unit ? <span className="settings-edit-input__unit">{unit}</span> : null}
                </div>
                {error ? (
                    <p className="settings-edit-error">
                        <i className="fas fa-exclamation-circle me-1" />
                        {error.message}
                    </p>
                ) : null}
            </div>
        );
    };

    const renderTextInput = (name, label, { placeholder, type = "text", pattern, patternMessage }) => {
        const error = getError(name);
        return (
            <div className="col-12">
                <label className="settings-edit-label">{label}</label>
                <div className="settings-edit-input-wrap">
                    <input
                        type={type}
                        placeholder={placeholder}
                        className={`settings-edit-input ${error ? "is-error" : ""}`}
                        {...register(name, {
                            setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                            validate: (v) => !v || pattern.test(v) || patternMessage,
                        })}
                    />
                </div>
                {error ? (
                    <p className="settings-edit-error">
                        <i className="fas fa-exclamation-circle me-1" />
                        {error.message}
                    </p>
                ) : null}
            </div>
        );
    };

    const renderInputWithUnit = (name, unitName, label, options, colClass = "col-md-6") => {
        const error = getError(name);
        return (
            <div className={colClass}>
                <label className="settings-edit-label">{label}</label>
                <div className={`settings-edit-unit-group ${error ? "is-error" : ""}`}>
                    <input
                        type="text"
                        placeholder="0.00"
                        className="settings-edit-unit-group__input"
                        {...register(name, {
                            required: "Required",
                            pattern: {
                                value: validNumberRegex,
                                message: "Invalid format",
                            },
                        })}
                    />
                    <select className="settings-edit-unit-group__select" {...register(unitName)}>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                {error ? (
                    <p className="settings-edit-error">
                        <i className="fas fa-exclamation-circle me-1" />
                        {error.message}
                    </p>
                ) : null}
            </div>
        );
    };

    return (
        <div className="app-content content dashboard">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="edit-pricing">
                        <div className="user-details-wrapper settings-page settings-edit-page">
                            <div className="settings-page__hero settings-edit-page__hero">
                                <div>
                                    <BackButton />
                                    <h1 className="pageTitle mb-0 mt-2">Edit System Settings</h1>
                                    <p className="settings-page__subtitle">
                                        Adjust global app configurations, logistics rates, and platform policies.
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit(submit)} className="settings-edit-form">
                                <div className="row g-4">
                                    <div className="col-xl-8 col-lg-7 settings-edit-main">
                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Base & Distance Rates",
                                                "Mileage and platform commission — base fee is set in Vehicle Type Management",
                                                "route"
                                            )}
                                            <div className="row g-3">
                                                {/* Base pickup fee removed — included in each vehicle type base price */}
                                                {renderInput("freeDistanceMiles", "Free Miles Included")}
                                                {renderInput("pricePerMile", "Price per Extra Mile ($)")}
                                                {renderInput("adminCommission", "Platform Fee (%)")}
                                            </div>
                                            <div className="settings-edit-note">
                                                <i className="fas fa-info-circle" />
                                                Base pickup fee is not charged separately. Set the full base price on each vehicle type under Vehicle Type Management.
                                            </div>
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Search & Matching Configuration",
                                                "Driver discovery radius and notification limits",
                                                "search"
                                            )}
                                            <div className="row g-3">
                                                {renderInputWithUnit("radiusDistance", "radiusUnit", "Driver Search Radius", [
                                                    { label: "Miles", value: "miles" },
                                                    { label: "KM", value: "km" },
                                                ], "col-12")}
                                                {renderInputWithUnit("timeoutTime", "timeoutUnit", "Search Timeout", [
                                                    { label: "Seconds", value: "sec" },
                                                    { label: "Minutes", value: "min" },
                                                ], "col-12 col-md-6")}
                                                {renderInput("maxDriverNotifications", "Max Drivers to Notify", "", "col-12 col-md-6")}
                                            </div>
                                            <div className="settings-edit-note">
                                                <i className="fas fa-info-circle" />
                                                This radius defines how far the system searches for available drivers from the pickup location.
                                            </div>
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Weight Based Surcharge",
                                                "Extra charges for heavier packages",
                                                "weight"
                                            )}
                                            <div className="row g-3">
                                                {renderInput("baseWeightLbs", "Standard Weight Limit (lbs)", "", "col-md-12 col-lg-6")}
                                                {renderInput("addWeightPlusLbs", "Weight Step Unit (lbs)", "", "col-md-12 col-lg-6")}
                                                {renderInput("stepPrice", "Price per Weight Step ($)", "", "col-12")}
                                            </div>
                                            <div className="settings-edit-note">
                                                <i className="fas fa-info-circle" />
                                                Example: Standard is 50lbs. Every 50lbs extra costs the step price.
                                            </div>
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "First Delivery Offer Settings",
                                                "First booking free or percentage off — separate from review reward",
                                                "firstDelivery",
                                                renderToggle("firstDeliveryOfferStatus")
                                            )}
                                            {watch("firstDeliveryOfferStatus") ? (
                                                <>
                                                    {renderFirstDeliveryOfferTypeSelector()}
                                                    <div className="row g-3">
                                                        {!isFirstDeliveryOfferFree ? (
                                                            renderInput(
                                                                "firstDeliveryOfferDiscountPercent",
                                                                "Discount Percentage (%)",
                                                                "",
                                                                "col-md-6"
                                                            )
                                                        ) : (
                                                            <div className="col-md-6">
                                                                <label className="settings-edit-label">Offer Value</label>
                                                                <div className="settings-promo-preview">
                                                                    <div>
                                                                        <div className="settings-promo-preview__label">First delivery</div>
                                                                        <p className="settings-promo-preview__text mb-0">Full delivery fare waived</p>
                                                                    </div>
                                                                    <span className="settings-promo-preview__value">FREE</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="settings-edit-note">
                                                        <i className="fas fa-info-circle" />
                                                        Applies automatically on the user's first completed booking. This is not linked to the review reward offer below.
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="settings-edit-empty">
                                                    <i className="fas fa-toggle-off" />
                                                    <p className="mb-0">First delivery offer is disabled.</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Review Offer Settings",
                                                "Discount earned after the user rates a completed delivery",
                                                "promo",
                                                renderToggle("isreviewDiscountEnabled")
                                            )}
                                            {watch("isreviewDiscountEnabled") ? (
                                                <>
                                                    <div className="row g-3">
                                                        <div className="col-12">
                                                            <label className="settings-edit-label">Discount Type</label>
                                                            <select className="settings-edit-input settings-edit-input--select" {...register("reviewDiscountOfferType")}>
                                                                <option value="percentage">Percentage (%)</option>
                                                                <option value="fixed">Flat Price ($)</option>
                                                            </select>
                                                        </div>
                                                        {renderInput(
                                                            "reviewDiscountOffer",
                                                            "Review Reward Discount",
                                                            watch("reviewDiscountOfferType") === "percentage" ? "%" : "$",
                                                            "col-md-6"
                                                        )}
                                                        <div className="col-md-6">
                                                            <label className="settings-edit-label">Apply on Booking Number</label>
                                                            <div className="settings-edit-input-wrap">
                                                                <input
                                                                    type="text"
                                                                    placeholder="2"
                                                                    className={`settings-edit-input ${getError("bookingNumber") ? "is-error" : ""}`}
                                                                    {...register("bookingNumber", {
                                                                        required: "Required",
                                                                        validate: {
                                                                            integer: (v) => Number.isInteger(Number(v)) || "Must be an integer",
                                                                            minVal: (v) => Number(v) >= 2 || "Must be 2 or higher",
                                                                        }
                                                                    })}
                                                                />
                                                            </div>
                                                            {getError("bookingNumber") ? (
                                                                <p className="settings-edit-error">
                                                                    <i className="fas fa-exclamation-circle me-1" />
                                                                    {getError("bookingNumber").message}
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className="settings-edit-note settings-edit-note--success d-block">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="fas fa-info-circle text-success" />
                                                            <strong style={{ fontSize: "0.9rem" }}>How Review Reward Discount Works:</strong>
                                                        </div>
                                                        <div style={{ fontSize: "0.85rem", lineHeight: "1.5" }}>
                                                            <p className="mb-2">
                                                                The review discount is a reward for rating a completed ride. A user cannot rate a ride before completing at least one booking. Therefore, setting the value to 1 is invalid.
                                                            </p>
                                                            <div className="mb-2"><strong>Sequence Flow:</strong></div>
                                                            <ul className="ps-3 mb-2" style={{ listStyleType: "decimal" }}>
                                                                <li className="mb-1"><strong>Booking #1 (First Ride):</strong> User books, completes, and rates the driver.</li>
                                                                <li className="mb-1"><strong>Offer Unlocked:</strong> Upon submitting the rating, the discount is unlocked.</li>
                                                                <li className="mb-0"><strong>Booking #2 (Discount Active):</strong> The reward discount is automatically applied to this subsequent booking.</li>
                                                            </ul>
                                                            <p className="text-danger mb-0 mt-2" style={{ fontWeight: "700" }}>
                                                                <i className="fas fa-exclamation-triangle me-1" />
                                                                <strong>Validation Notice:</strong> The system enforces a minimum booking number of <strong>2</strong>. Setting this to 1 is blocked.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="settings-edit-empty">
                                                    <i className="fas fa-toggle-off" />
                                                    <p className="mb-0">Review offer is currently disabled.</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Driver Payout Schedule",
                                                "Informational label for drivers. Changes apply from next month only.",
                                                "payout"
                                            )}

                                            {(() => {
                                                const getOrd = (n) => {
                                                    const s = ['th','st','nd','rd'];
                                                    const v = n % 100;
                                                    return n + (s[(v-20)%10] || s[v] || s[0]);
                                                };
                                                const at = flattenedSettings._activePayoutCycleType;
                                                let activeLabel = 'Semi-Monthly';
                                                if (at === 'weekly') activeLabel = `Weekly (Every ${(flattenedSettings._activePayoutWeeklyDay || 'monday').charAt(0).toUpperCase() + (flattenedSettings._activePayoutWeeklyDay || 'monday').slice(1)})`;
                                                else if (at === 'monthly') activeLabel = `Monthly (${getOrd(flattenedSettings._activePayoutMonthlyDay || 1)} of month)`;
                                                else activeLabel = `Semi-Monthly (${getOrd(1)}–${getOrd(flattenedSettings._activePayoutCycleSplitDay || 15)} & ${getOrd((flattenedSettings._activePayoutCycleSplitDay || 15) + 1)}–End)`;

                                                return (
                                                    <div className="settings-edit-note settings-edit-note--info d-block mb-4">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="fas fa-info-circle text-primary" />
                                                            <strong style={{ fontSize: "0.9rem" }}>Current Active Schedule:</strong>
                                                        </div>
                                                        <p className="mb-0" style={{ fontSize: "0.85rem" }}>
                                                            <strong>Active:</strong> {activeLabel}
                                                        </p>
                                                        {flattenedSettings._payoutSettingsChangeEffectiveDate && (
                                                            <p className="mb-0 mt-2 text-warning" style={{ fontSize: "0.85rem", fontWeight: "700" }}>
                                                                <i className="fas fa-clock me-1" />
                                                                Pending change scheduled for {new Date(flattenedSettings._payoutSettingsChangeEffectiveDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <label className="settings-edit-label">Payout Cycle Type (Next Month)</label>
                                                    <select className="settings-edit-input settings-edit-input--select" {...register("payoutCycleType")}>
                                                        <option value="semi-monthly">Semi-Monthly (2 cycles per month)</option>
                                                        <option value="weekly">Weekly</option>
                                                        <option value="monthly">Monthly (1 cycle per month)</option>
                                                    </select>
                                                </div>

                                                {watch("payoutCycleType") === "weekly" && (
                                                    <div className="col-12 col-md-6">
                                                        <label className="settings-edit-label">Payout Day of Week</label>
                                                        <select className="settings-edit-input settings-edit-input--select" {...register("payoutWeeklyDay")}>
                                                            <option value="monday">Monday</option>
                                                            <option value="tuesday">Tuesday</option>
                                                            <option value="wednesday">Wednesday</option>
                                                            <option value="thursday">Thursday</option>
                                                            <option value="friday">Friday</option>
                                                            <option value="saturday">Saturday</option>
                                                            <option value="sunday">Sunday</option>
                                                        </select>
                                                    </div>
                                                )}

                                                {watch("payoutCycleType") === "semi-monthly" && (
                                                    <div className="col-12 col-md-6">
                                                        <label className="settings-edit-label">Split Day (Cycle 1 ends on this date)</label>
                                                        <select className="settings-edit-input settings-edit-input--select" {...register("payoutCycleSplitDay")}>
                                                            {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                                                <option key={d} value={d}>
                                                                    {d === 15 ? `${d} (Default — 1st–15th & 16th–End)` : `${d} (1st–${d}th & ${d+1}th–End)`}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {watch("payoutCycleType") === "monthly" && (
                                                    <div className="col-12 col-md-6">
                                                        <label className="settings-edit-label">Payout Day of Month</label>
                                                        <select className="settings-edit-input settings-edit-input--select" {...register("payoutMonthlyDay")}>
                                                            {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                                                <option key={d} value={d}>{d === 1 ? `${d}st (Default)` : d}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="settings-edit-note mt-3">
                                                <i className="fas fa-info-circle" />
                                                Any changes made here will take effect from the 1st of next month. The current month’s active schedule cannot be changed retroactively.
                                            </div>
                                        </div>


                                    </div>

                                    <div className="col-xl-4 col-lg-5 settings-edit-sidebar">
                                        {/* <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Post-Acceptance Penalty Settings",
                                                "Cancellation charges after driver acceptance",
                                                "penalty"
                                            )}
                                            <div className="settings-penalty-box settings-penalty-box--edit">
                                                <div>
                                                    <h6 className="settings-penalty-box__title">Cancellation Penalty Rate</h6>
                                                    <p className="settings-penalty-box__text mb-0">
                                                        Set the percentage deducted if a user cancels after a driver accepts the ride.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="settings-penalty-input">
                                                <input
                                                    type="number"
                                                    className="settings-penalty-input__field"
                                                    placeholder="e.g. 20"
                                                    min="0"
                                                    max="100"
                                                    {...register("afterAcceptanceCancellationFeePercent")}
                                                />
                                                <span className="settings-penalty-input__suffix">%</span>
                                            </div>
                                            <div className="settings-penalty-note">
                                                <i className="fas fa-shield-alt" />
                                                <p className="mb-0">
                                                    This penalty protects drivers from losing potential earnings due to late cancellations.
                                                </p>
                                            </div>
                                        </div> */}

                                        <input type="hidden" {...register("cancellationPolicyStatus")} value="true" />
                                        <input type="hidden" {...register("userCancellationFee")} value="0" />
                                        <input type="hidden" {...register("freeCancellationTime")} value="0" />

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Support Contact",
                                                "Shown on Help & Support in the user and driver apps",
                                                "support"
                                            )}
                                            <div className="row g-3">
                                                {renderTextInput("supportEmail", "Support Email", {
                                                    placeholder: "support@example.com",
                                                    type: "email",
                                                    pattern: SUPPORT_EMAIL_REGEX,
                                                    patternMessage: "Enter a valid email address",
                                                })}
                                                {renderTextInput("supportPhone", "Support Phone", {
                                                    placeholder: "+1 555 123 4567",
                                                    type: "tel",
                                                    pattern: SUPPORT_PHONE_REGEX,
                                                    patternMessage: "Enter a valid phone number with country code",
                                                })}
                                            </div>
                                            <div className="settings-edit-note">
                                                <i className="fas fa-info-circle" />
                                                Leave the phone empty to hide the Call option. Suspended or closed accounts also see these on the Help & Support screen.
                                            </div>
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Special Services",
                                                "Fragile items and helper assistance fees",
                                                "fees"
                                            )}
                                            <div className="row g-3">
                                                {renderInput("fragileItemFee", "Fragile Item Fee ($)", "/ item", "col-12")}
                                                {renderInput("extraHelperFee", "Helper Fee ($)", "/ helper", "col-12")}
                                            </div>
                                        </div>

                                        <div className="settings-section-card">
                                            {renderSectionHeader(
                                                "Insurance Configuration",
                                                "Optional insurance pricing for bookings",
                                                "insurance",
                                                renderToggle("insuranceStatus", "On", "Off")
                                            )}
                                            {watch("insuranceStatus") ? (
                                                <div className="row g-3">
                                                    <div className="col-12">
                                                        <label className="settings-edit-label">Insurance Type</label>
                                                        <select className="settings-edit-input settings-edit-input--select" {...register("insuranceType")}>
                                                            <option value="fixed">Flat Price ($)</option>
                                                            <option value="percentage">Percentage (%)</option>
                                                        </select>
                                                    </div>
                                                    {renderInput(
                                                        "insuranceValue",
                                                        "Insurance Coverage Price",
                                                        watch("insuranceType") === "percentage" ? "%" : "$",
                                                        "col-12"
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="settings-edit-empty">
                                                    <i className="fas fa-shield-alt" />
                                                    <p className="mb-0">Insurance is currently disabled. Enable it to configure pricing.</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="settings-edit-actions">
                                            <Button
                                                type="submit"
                                                className="siteBtn settings-edit-actions__save"
                                                loading={mutation.isLoading}
                                            >
                                                Save Changes
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/settings")}
                                                className="settings-edit-actions__cancel"
                                            >
                                                Discard and Return
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
